import { getSettings } from "../db/schema";
import { svgToPngDataUrl } from "./settings";

const PDF_TIMEOUT_MS = 45000;

function timeoutError() {
  return new Error("PDF build timed out during building_pdf after 45 seconds.");
}

function workerError() {
  return new Error("PDF worker failed during building_pdf.");
}

async function dataUrlToPngBlob(dataUrl) {
  const binary = atob(dataUrl.split(",")[1]);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return { blob: new Blob([bytes], { type: "image/png" }), bytes };
}

// pdf-lib cannot embed SVG: rasterize to PNG on the main thread first.
// On failure the logo is silently dropped (the PDF still builds).
async function resolveLogoPayload(settings) {
  const logo = settings?.logo;

  if (!logo) return null;

  try {
    if (logo.type === "image/svg+xml") {
      const { blob, bytes } = await (async () => {
        const dataUrl = await svgToPngDataUrl(logo.blob);

        return dataUrlToPngBlob(dataUrl);
      })();

      return { bytes, width: logo.width, height: logo.height };
    }

    const bytes = new Uint8Array(await logo.blob.arrayBuffer());

    return { bytes, width: logo.width, height: logo.height };
  } catch {
    return null;
  }
}

function createWorker() {
  if (typeof Worker === "undefined") {
    throw new Error("PDF worker is unavailable.");
  }

  return new Worker(new URL("../workers/pdf.worker.js", import.meta.url), {
    type: "module"
  });
}

async function buildPdfOnMainThread(payload, onProgress) {
  const { buildPdf } = await import("./pdf-build");

  const bytes = await buildPdf(payload, onProgress);

  return new Blob([bytes], { type: "application/pdf" });
}

function createWorkerPdf(payload, onProgress) {
  let worker;

  try {
    worker = createWorker();
  } catch {
    return buildPdfOnMainThread(payload, onProgress);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let receivedMessage = false;

    const finish = (callback, value) => {
      if (settled) return;

      settled = true;
      worker.terminate();
      callback(value);
    };

    worker.onmessage = ({ data }) => {
      receivedMessage = true;

      if (data?.progress !== undefined && !data.done && !data.error) {
        onProgress?.(data.progress);
        return;
      }

      if (data?.error) {
        finish(reject, new Error(data.error));
        return;
      }

      if (data?.done && data.pdf) {
        onProgress?.(100);
        finish(resolve, new Blob([data.pdf], { type: "application/pdf" }));
      }
    };

    worker.onerror = () => {
      if (!receivedMessage) {
        finish(resolve, buildPdfOnMainThread(payload, onProgress));
        return;
      }

      finish(reject, workerError());
    };

    worker.onmessageerror = () => {
      finish(reject, workerError());
    };

    try {
      worker.postMessage(payload);
    } catch {
      finish(resolve, buildPdfOnMainThread(payload, onProgress));
    }
  });
}

function withPdfTimeout(promise) {
  let timer;

  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => {
      reject(timeoutError());
    }, PDF_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => {
    window.clearTimeout(timer);
  });
}

export async function createPdf(payload, onProgress) {
  try {
    const settings = await getSettings();
    const logo = await resolveLogoPayload(settings);

    const full = {
      ...payload,
      business: settings.business,
      watermark: settings.watermark,
      logo
    };

    return await withPdfTimeout(createWorkerPdf(full, onProgress));
  } catch (error) {
    throw error instanceof Error ? error : new Error("PDF build failed.");
  }
}
